import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classes from './DesktopAssessmentSummary.module.scss';
import {
  collectAssessmentRecommendations,
  getDesktopAssessmentLabels,
} from 'utils/desktopAssessment';

const renderLevel = (label, value) => (
  <div className={classes.levelRow} key={label}>
    <span>{label}</span>
    <strong>{value ?? '—'}</strong>
  </div>
);

const getStatusLabel = (assessment, labels) => {
  if (assessment?.isLoading) return 'Loading assessment...';

  if (assessment?.attempt?.summary?.canProduceReport || assessment?.attempt?.summary?.canProduceCustomLevel || assessment?.attempt?.summary?.canRecommendPurchase) {
    return labels.ready;
  }

  if (assessment?.attemptId) return labels.progress;
  return labels.idle;
};

const playTextAudio = (text) => {
  if (!text || typeof window === 'undefined' || !window.speechSynthesis) {
    return;
  }

  try {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
  } catch (error) {
    console.warn('[desktop-assessment][alert-audio-error]', error);
  }
};

const DesktopAssessmentSummary = ({ assessment, translate, onStart, onResume, onRestart, onViewReport }) => {
  const lastAlertCodeRef = useRef(null);
  const mode = assessment?.mode || 'mobile';
  const labels = getDesktopAssessmentLabels(mode);
  const summary = assessment?.attempt?.summary || null;
  const levels = summary?.levels || {};
  const recommendations = collectAssessmentRecommendations(assessment?.attempt);
  const freeTrialMissing = assessment?.enabled && !assessment?.freeTrialGroup;
  const validation = assessment?.assessmentGroupValidation;
  const unfinishedAlert = summary?.unfinishedAlert;
  const report = summary?.report || assessment?.reportSnapshot?.report || null;
  const isOnlineMode = mode === 'online';

  useEffect(() => {
    const alertKey = unfinishedAlert?.code || unfinishedAlert?.text || null;

    if (alertKey && alertKey !== lastAlertCodeRef.current) {
      lastAlertCodeRef.current = alertKey;
      playTextAudio(unfinishedAlert.text);
    }
  }, [unfinishedAlert?.code, unfinishedAlert?.text]);

  return (
    <section className={classes.wrapper}>
      <div className={classes.header}>
        <div>
          <h3>{labels.title}</h3>
          <p>{getStatusLabel(assessment, labels)}</p>
        </div>

        <div className={classes.actions}>
          {!assessment?.attemptId && (
            <button type="button" onClick={onStart} disabled={assessment?.isLoading || freeTrialMissing || validation?.isValid === false}>
              {labels.start}
            </button>
          )}
          {!!assessment?.attemptId && !summary?.canRecommendPurchase && (
            <button type="button" onClick={onResume} disabled={assessment?.isLoading || freeTrialMissing || validation?.isValid === false}>
              {labels.resume}
            </button>
          )}
          {!!report && (
            <button type="button" onClick={onViewReport} disabled={assessment?.isLoading}>
              View Report
            </button>
          )}
          {!!assessment?.attemptId && !isOnlineMode && (
            <button type="button" onClick={onRestart} disabled={assessment?.isLoading || freeTrialMissing || validation?.isValid === false} className={classes.secondaryButton}>
              {labels.restart}
            </button>
          )}
        </div>
      </div>

      {freeTrialMissing && (
        <div className={classes.warning}>
          Free Trial / Assessment activity group was not found in this solution menu.
        </div>
      )}

      {validation?.isValid === false && !freeTrialMissing && (
        <div className={classes.warning}>{validation.message}</div>
      )}

      {validation?.isValid && validation?.hasHelp === false && (
        <div className={classes.helpWarning}>{validation.message}</div>
      )}

      {unfinishedAlert?.text && (
        <div className={classes.alertBox}>
          <span>{unfinishedAlert.text}</span>
          <button type="button" className={classes.audioButton} onClick={() => playTextAudio(unfinishedAlert.text)}>
            Play audio
          </button>
        </div>
      )}

      {!!assessment?.attemptId && (
        <div className={classes.metaRow}>
          <span>Attempt ID: {assessment.attemptId}</span>
          <span>Mode: {mode}</span>
          <span>Pending: {(summary?.pendingActivityKeys || []).length}</span>
        </div>
      )}

      {summary && (
        <div className={classes.content}>
          <div className={classes.card}>
            <h4>{labels.output}</h4>
            {renderLevel('Phonics', levels.phonics)}
            {renderLevel('Vocabulary', levels.vocabulary)}
            {renderLevel('Reading', levels.reading)}
            {renderLevel('Writing', levels.writing)}
          </div>

          <div className={classes.card}>
            <h4>Recommendations</h4>
            {recommendations.length ? (
              <ul>
                {recommendations.map((item, index) => (
                  <li key={`${item.code}-${index}`}>{item.text || item.code}</li>
                ))}
              </ul>
            ) : (
              <p>No recommendations yet.</p>
            )}
          </div>
        </div>
      )}

      {report && (
        <div className={classes.reportCard}>
          <h4>{report?.title || 'Diagnostic Assessment Report'}</h4>
          <p className={classes.reportSubtext}>
            This report remains available from the assessment summary for this solution.
          </p>
          <div className={classes.reportGrid}>
            <div>
              <strong>Age band:</strong> {report?.ageBand || '—'}
            </div>
            <div>
              <strong>Language:</strong> {report?.language || '—'}
            </div>
            {renderLevel('Phonics', report?.levels?.phonics)}
            {renderLevel('Vocabulary', report?.levels?.vocabulary)}
            {renderLevel('Reading', report?.levels?.reading)}
            {renderLevel('Writing', report?.levels?.writing)}
          </div>
        </div>
      )}
    </section>
  );
};

DesktopAssessmentSummary.propTypes = {
  assessment: PropTypes.object,
  translate: PropTypes.func,
  onStart: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onRestart: PropTypes.func.isRequired,
  onViewReport: PropTypes.func,
};

export default DesktopAssessmentSummary;
